import React from "react";
import { Popover, Tag } from "antd";
import * as XLSX from "xlsx";
import ExcelUploadStageModal, {
  ExcelUploadTypeConfig,
  UploadRowBucket,
} from "../../../components/upload/shared/ExcelUploadStageModal";
import { UploadReportState } from "../../../components/upload/shared/uploadReport";
import { executionNormApis } from "../../../api/ActivityExecution";

// Bulk upload of the execution norm matrix — one row per CELL:
// {Activity Name, BOQ, Slot Name, Norm Value}.
//
// Four things about this upload are easy to get wrong, and all four are spelled
// out in the template's Instructions sheet as well as here:
//
//  1. Committing REPLACES the full matrix of every activity the file mentions.
//     A cell left out of the file is deleted.
//  2. BOQ is `tasks.internalTaskNumber` (the bare 'B3.1.1'), and the norm is
//     applied on (Activity, BOQ). Where the WBS split that BOQ line by rock
//     class into two tasks, each takes only its own class's norm.
//  3. A BLANK Norm Value is read as ZERO, not as "skip this row".
//  4. A row that cannot land is a WARNING, not an error — the WBS simply does
//     not carry that BOQ line for that activity.

const generateExecutionNormsTemplate = () => {
  const headers = ["Activity Name", "BOQ", "Slot Name", "Norm Value", "Driver UOM"];
  const sampleRows = [
    ["ADIT-1A EXCAVATION", "B1.1", "Class-I", 0, "Mt"],
    ["", "B1.1", "Class-II", 320.485283946051, ""],
    ["", "B1.1", "Class-III", 327.258981138537, ""],
    ["", "B3.1.1", "Class-II", 42.298, ""],
    ["", "B3.1.1", "Class-III", 43.192, ""],
    ["", "B3.1.1", "Class-IV", "", ""],
    ["Excavation of Power House & Control Block", "B3.3.1", "Mechanised Excavation — In the Vault (Upto EL ±1253.0m)", 1, "CUM"],
  ];
  const dataSheet = XLSX.utils.aoa_to_sheet([headers, ...sampleRows]);
  dataSheet["!cols"] = [{ wch: 42 }, { wch: 12 }, { wch: 46 }, { wch: 22 }, { wch: 12 }];

  const instructions = [
    ["Execution Norms Upload — Instructions"],
    [""],
    ["What a norm is", "The BOQ quantity produced per ONE unit of advance in that slot. B1.1 Dewatering = 327.258981138537 KWh per metre of Class-III advance."],
    [""],
    ["Activity Name", "Required on the FIRST row of each activity's block; leave it blank on the rows below and they carry down. Matched to the project's activity by name — the estimator's 'ADIT-1A EXCAVATION' finds the WBS's 'Adit-1A - UG-Excv(AE-0100)'. Row ORDER is never used."],
    ["BOQ", "Required. The bare BOQ number as held on the task ('B3.1.1'), not the full 'AE-0100/B3.1.1'. The norm is applied on Activity + BOQ."],
    ["Slot Name", "Required. 'Class-I'..'Class-V' for rock-class activities, or the method/zone label for caverns and shafts excavated by method (e.g. 'DBM Excavation — Below Vault'). Labels are matched to the activity's existing slots, so re-uploading cannot re-map a slot."],
    ["Norm Value", "Give the FULL precision from your source — do not round. Rounding the norm (rather than the product) drifts B1.1 by about 4 KWh over a 400 m drive."],
    ["Driver UOM", "Optional. The unit the ADVANCE is measured in — 'Mt' for tunnel advance, 'CUM' for method/zone caverns. Not the BOQ line's own unit."],
    [""],
    ["BLANK means ZERO", "An empty Norm Value is saved as 0, not skipped. A zero contributes nothing when the face advances, so a full BOQ x Slot grid with empty cells is exactly right."],
    ["Split BOQ lines", "Where the WBS splits one BOQ line into per-rock-class tasks (B3.1.1 and B3.1.1(1)), each task takes ONLY its own class's norm — the Class-II row goes to the Class-II task. You do not need to do anything: give one row per class and the import sorts it out."],
    ["Replace rule", "Committing REPLACES the ENTIRE matrix of every activity the file mentions, and leaves every other activity untouched. Include each activity's complete matrix."],
    ["Warnings", "A row whose BOQ line has no task under that activity is reported as a WARNING and skipped — the WBS does not carry that line there. It does not stop the rest of the file saving."],
    ["Five slots max", "An activity has five driver slots. A sixth distinct Slot Name under one activity is rejected."],
    ["Duplicates", "The same task + slot may appear only once in the file."],
  ];
  const instructionSheet = XLSX.utils.aoa_to_sheet(instructions);
  instructionSheet["!cols"] = [{ wch: 22 }, { wch: 130 }];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, dataSheet, "Execution Norms");
  XLSX.utils.book_append_sheet(workbook, instructionSheet, "Instructions");
  XLSX.writeFile(workbook, "execution-norms-upload-template.xlsx");
};

const rowErrors = (record: any): string[] =>
  Array.isArray(record?.Error) ? record.Error : record?.Error ? [record.Error] : [];

const previewColumns: any[] = [
  {
    title: "Status",
    key: "rowStatus",
    width: 96,
    fixed: "left",
    render: (_: any, record: any) => {
      const errors = rowErrors(record);
      if (errors.length) {
        return (
          <Popover
            placement="right"
            title={<span style={{ color: "red" }}>Row Errors</span>}
            content={
              <div style={{ maxWidth: 320 }}>
                {errors.map((err, i) => (
                  <div key={i} style={{ marginBottom: 4 }}>
                    • {err}
                  </div>
                ))}
              </div>
            }
          >
            <Tag color="red">Error</Tag>
          </Popover>
        );
      }
      if (record?.status === "Applied") return <Tag color="blue">Saved</Tag>;
      if (record?.status === "Warning") return <Tag color="gold">Warning</Tag>;
      // A zero norm that had nowhere to land — nothing would ever derive from
      // it, so it is not worth flagging.
      if (record?.status === "Skipped")
        return <Tag style={{ color: "#8094ae" }}>Not applicable</Tag>;
      return <Tag color="green">Valid</Tag>;
    },
  },
  { title: "Activity", dataIndex: "Activity", key: "Activity", width: 230, ellipsis: true },
  { title: "BOQ", dataIndex: "BOQ", key: "BOQ", width: 100 },
  { title: "Task", dataIndex: "taskName", key: "taskName", width: 210, ellipsis: true },
  { title: "Slot", dataIndex: "Slot", key: "Slot", width: 200, ellipsis: true },
  {
    title: "Norm Value",
    dataIndex: "Norm Value",
    key: "normValue",
    width: 160,
    align: "right" as const,
    // Blank in the file means zero — say so rather than leaving the cell empty.
    render: (value: any) =>
      value === "" || value === undefined || value === null ? (
        <span style={{ color: "#8094ae" }}>0 (blank)</span>
      ) : (
        <span className="tabular-nums">{value}</span>
      ),
  },
  {
    title: "Warning",
    dataIndex: "Warning",
    key: "Warning",
    width: 300,
    render: (value: any) =>
      value ? <span style={{ color: "#b58a1f", fontSize: 12 }}>{value}</span> : null,
  },
  {
    title: "Error Details",
    key: "errorDetails",
    width: 280,
    render: (_: any, record: any) => {
      const errors = rowErrors(record);
      if (!errors.length) return null;
      return <span style={{ color: "#e85347", fontSize: 12 }}>{errors.join("; ")}</span>;
    },
  },
];

const bucketRows = (rows: any[]): UploadRowBucket[] => {
  const errorRows = rows.filter((row) => row?.Error);
  const warningRows = rows.filter((row) => !row?.Error && row?.status === "Warning");
  const skippedRows = rows.filter((row) => !row?.Error && row?.status === "Skipped");
  const willSave = rows.filter(
    (row) => !row?.Error && row?.status !== "Warning" && row?.status !== "Skipped"
  );
  return [
    { key: "all", label: `All (${rows.length})`, rows },
    { key: "valid", label: `Will save (${willSave.length})`, rows: willSave },
    { key: "warnings", label: `Warnings (${warningRows.length})`, rows: warningRows },
    { key: "skipped", label: `Not applicable (${skippedRows.length})`, rows: skippedRows },
    { key: "errors", label: `Errors (${errorRows.length})`, rows: errorRows },
  ];
};

const summaryTags = (report: UploadReportState) => {
  const activities = new Set(
    report.rows.filter((row: any) => !row?.Error).map((row: any) => row?.Activity).filter(Boolean)
  );
  const warnings = report.rows.filter(
    (row: any) => !row?.Error && row?.status === "Warning"
  ).length;
  return (
    <>
      {!!activities.size && (
        <Tag color="processing">{activities.size} activity matrix(es)</Tag>
      )}
      {!!warnings && <Tag color="gold">{warnings} skipped — no such BOQ line</Tag>}
    </>
  );
};

const ExecutionNormsUploadModal = ({
  open,
  onClose,
  onSuccess,
  projectId,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  projectId: number | string;
}) => {
  const config: ExcelUploadTypeConfig = React.useMemo(
    () => ({
      title: "Execution Norms (Excel)",
      entityLabel: "Execution Norms",
      templateSource: { kind: "client", generate: generateExecutionNormsTemplate },
      startUpload: (formData) => executionNormApis.uploadExcel(formData),
      buildBody: (comments) => ({
        projectId: String(projectId),
        comment: comments ? comments : "N/A",
      }),
      previewColumns,
      previewScrollX: 1580,
      previewPageSize: 100,
      bucketRows,
      summaryTags,
      fullScreen: true,
      confirmClose: true,
      reportExport: {
        fileName: "execution-norms-upload-report.xlsx",
        columns: [
          {
            header: "Status",
            width: 14,
            value: (row: any) =>
              row.Error
                ? "Error"
                : row.status === "Applied"
                ? "Saved"
                : row.status === "Warning"
                ? "Warning"
                : row.status === "Skipped"
                ? "Not applicable"
                : "Valid",
          },
          { header: "Activity", width: 40, value: (row: any) => row.Activity || "" },
          { header: "BOQ", width: 12, value: (row: any) => row.BOQ || "" },
          { header: "Task", width: 32, value: (row: any) => row.taskName || "" },
          { header: "Slot", width: 30, value: (row: any) => row.Slot || "" },
          { header: "Norm Value", width: 22, value: (row: any) => row["Norm Value"] ?? "" },
          { header: "Warning", width: 52, value: (row: any) => row.Warning || "" },
          {
            header: "Error Details",
            width: 48,
            value: (row: any) =>
              Array.isArray(row.Error) ? row.Error.join("; ") : row.Error || "",
          },
        ],
      },
      rowErrorLabel: (row: any) =>
        row.BOQ ? `${row.Activity || ""} ${row.BOQ}`.trim() : "Row",
      copy: {
        uploadIntro: (
          <p>
            Load the execution norm matrix — one row per cell:{" "}
            <b>Activity Name | BOQ | Slot Name | Norm Value</b>. A norm is the BOQ quantity
            produced per <b>one unit</b> of advance in that slot. An empty Norm Value is saved
            as <b>0</b>. Committing <b>replaces the full matrix of every activity in the
            file</b> and leaves other activities untouched. You'll see a preview before
            anything is saved.
            <br />
            <br />
            {/* Uploads are a bulk load, not a dated business decision — so the
                effective date follows a fixed rule rather than a form field.
                Anyone who needs a different date saves a version on the matrix
                screen, which asks for one explicitly. */}
            <b>When it takes effect:</b> an activity being configured for the first time gets a
            matrix covering the whole project, so any back-dated entry resolves. An activity that
            already has one gets a <b>new version effective today</b> — days already captured keep
            the figures they were derived with. Use <b>Restate days</b> on the norms screen if
            those older figures should move too.
          </p>
        ),
        validatingHeading: "Validating your file…",
        validatingSub:
          "Matching each activity by name and each BOQ line to a task. Where the WBS split a BOQ line by rock class, each task is given only its own class's norm.",
        committingHeading: "Saving execution norms…",
        committingSub:
          "Replacing the norm matrix of each activity in the file. Valid rows save; rows with no matching BOQ line are skipped.",
        commitCta: "Save Execution Norms",
        doneHeading: "Execution norms saved",
        doneSub: (report: UploadReportState) => {
          const warnings = report.rows.filter(
            (row: any) => !row?.Error && row?.status === "Warning"
          ).length;
          return (
            <>
              {report.successCount} cell(s) saved
              {report.failureCount ? `, ${report.failureCount} failed` : ""}
              {warnings ? `, ${warnings} skipped for having no matching BOQ line` : ""}. Daily
              execution can now be captured for these activities.
            </>
          );
        },
        creationFailedNote: "Some rows could not be saved — see the errors in the preview.",
      },
      discardOnCancelFromPreview: false,
    }),
    [projectId]
  );

  return (
    <ExcelUploadStageModal
      config={config}
      open={open}
      onClose={onClose}
      onSuccess={onSuccess}
      projectId={projectId}
    />
  );
};

export default ExecutionNormsUploadModal;
